import React from 'react';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import { setModalContent, setShowModal } from '../utils/utils.slice';
import {
  CommentType,
  CreateOrderCommentDto,
  CreateOrderDto,
  CreateOrderTemplateDto,
  NewOrderType,
  DeviceCategory,
  OrderStatus,
  OrderTemplateType,
  OrderType,
  DeviceReportItemType,
} from '../../types/order/order.type';
import { ATA_TYPE } from '../../utils/constants';
import { setNewOrderDetails, setNewOrderDetailsFetchStatus } from './orders.slice';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import DIDErrorModal from '../../components/Modal/DIDErrorModal/DIDErrorModal';
import { parseApiError, rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';
import { UserType } from '../../types/user/user.type';
import {
  CreateRMAOrderDto,
  CreateShippingPackageDto,
  RMAOrderListItemType,
  RMAOrderType,
} from '../../views/Main/RMAOrders/utils/rmaOrders.types';
import {
  CreateOrderSimActivationRequestPayload,
  OrderSimActivationRequestType,
} from '../../types/order/order-sim-activation.type';

export const postOrderDetails = createAsyncThunk<OrderType, { data: unknown }, { state: RootReducerState }>(
  'orders/postOrderDetails',
  async ({ data }, thunkAPI) => {
    try {
      const response = await axios.post<OrderType>(`${process.env.REACT_APP_API_URL}/orders`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      const errorMessage = parseApiError({ error, fallback: 'Error during creating order!' });

      if (errorMessage.toLowerCase().includes('can not buy did')) {
        const handleModalClose = (result: { success: boolean; submitWithoutLines?: boolean }) => {
          thunkAPI.dispatch(setShowModal(false));
          thunkAPI.dispatch(setModalContent(null));

          if (result.success && result.submitWithoutLines) {
            const dataWithoutLines = data instanceof FormData ? data : { ...(data as any) };
            if (dataWithoutLines instanceof FormData) {
              const bodyString = dataWithoutLines.get('body') as string;
              if (bodyString) {
                const bodyData = JSON.parse(bodyString);
                if (bodyData.lineTypes) {
                  bodyData.lineTypes = [];
                }
                if (bodyData.lineTypesKnown) {
                  bodyData.lineTypesKnown = '0';
                }
                const newFormData = new FormData();
                newFormData.append('body', JSON.stringify(bodyData));
                const files = dataWithoutLines.getAll('files');
                files.forEach(file => {
                  newFormData.append('files', file);
                });
                thunkAPI.dispatch(postOrderDetails({ data: newFormData }));
              }
            } else {
              if (dataWithoutLines.lineTypes) {
                dataWithoutLines.lineTypes = [];
              }
              if (dataWithoutLines.lineTypesKnown) {
                dataWithoutLines.lineTypesKnown = '0';
              }
              thunkAPI.dispatch(postOrderDetails({ data: dataWithoutLines }));
            }
          }
        };

        thunkAPI.dispatch(
          setModalContent({
            content: React.createElement(DIDErrorModal, { onClose: handleModalClose }),
          })
        );
        thunkAPI.dispatch(setShowModal(true));
      }

      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const postOrderTemplateDetails = createAsyncThunk<
  OrderType,
  { data: CreateOrderTemplateDto },
  { state: RootReducerState }
>('orders/postOrderTemplateDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<OrderType>(`${process.env.REACT_APP_API_URL}/inventory/order-template`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating template!' });
  }
});

export const postRMAOrderDetails = createAsyncThunk<
  { id: number },
  { data: CreateRMAOrderDto },
  { state: RootReducerState }
>('orders/postRMAOrderDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{ id: number }>(`${process.env.REACT_APP_API_URL}/inventory/rma-order`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating order!' });
  }
});

export const postRMAShippingPackageDetails = createAsyncThunk<
  { success: boolean },
  { data: CreateShippingPackageDto; rmaOrderId: number },
  { state: RootReducerState }
>('orders/postRMAShippingPackageDetails', async ({ data, rmaOrderId }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/inventory/rma-order/${rmaOrderId}/shipping-package`,
      data
    );

    thunkAPI.dispatch(getRMAOrderDetails({ id: rmaOrderId }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating shipping package!' });
  }
});

export const postNewOrderDetails = createAsyncThunk<OrderType, { data: CreateOrderDto }, { state: RootReducerState }>(
  'orders/postNewOrderDetails',
  async ({ data }, thunkAPI) => {
    try {
      const response = await axios.post<OrderType>(`${process.env.REACT_APP_API_URL}/inventory/order`, data);
      return response.data;
    } catch (error) {
      const errorMessage = parseApiError({ error, fallback: 'Error during creating order!' });

      if (errorMessage.toLowerCase().includes('can not buy did')) {
        const handleModalClose = (result: { success: boolean; submitWithoutLines?: boolean }) => {
          thunkAPI.dispatch(setShowModal(false));
          thunkAPI.dispatch(setModalContent(null));

          if (result.success && result.submitWithoutLines) {
            const dataWithoutLines = { ...data } as any;
            if (dataWithoutLines.lineTypes) {
              dataWithoutLines.lineTypes = [];
            }
            if (dataWithoutLines.lineTypesKnown) {
              dataWithoutLines.lineTypesKnown = '0';
            }
            thunkAPI.dispatch(postNewOrderDetails({ data: dataWithoutLines }));
          }
        };

        thunkAPI.dispatch(
          setModalContent({
            content: React.createElement(DIDErrorModal, { onClose: handleModalClose }),
          })
        );
        thunkAPI.dispatch(setShowModal(true));
      }

      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const postConfirmDeviceAssignment = createAsyncThunk<
  { success: boolean },
  { id: number; data: { devices: Array<{ deviceId: number; vid: string }>; warehouse: { id: number } } },
  { state: RootReducerState }
>('orders/postConfirmDeviceAssignment', async ({ data, id }, thunkAPI) => {
  try {
    await axios.post(`${process.env.REACT_APP_API_URL}/inventory/order/${id}/confirm-device-assignment`, data);

    return { success: true };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during confirming devices assignment!' });
  }
});

export const postOrderSimActivationRequest = createAsyncThunk<
  OrderSimActivationRequestType,
  { id: number; data: CreateOrderSimActivationRequestPayload },
  { state: RootReducerState }
>('orders/postOrderSimActivationRequest', async ({ id, data }, thunkAPI) => {
  try {
    const response = await axios.post<OrderSimActivationRequestType>(
      `${process.env.REACT_APP_API_URL}/inventory/order/${id}/sim-activation-request`,
      data
    );
    await thunkAPI.dispatch(getNewOrderDetails({ id }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error sending SIM activation request!' });
  }
});

export const postResendOrderSimActivationRequest = createAsyncThunk<
  OrderSimActivationRequestType,
  { id: number },
  { state: RootReducerState }
>('orders/postResendOrderSimActivationRequest', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.post<OrderSimActivationRequestType>(
      `${process.env.REACT_APP_API_URL}/inventory/order/${id}/sim-activation-request/resend`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error resending SIM activation request!' });
  }
});

export const getOrderSimActivationRequest = createAsyncThunk<
  OrderSimActivationRequestType | null,
  { id: number },
  { state: RootReducerState }
>('orders/getOrderSimActivationRequest/no-loader', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<OrderSimActivationRequestType | null>(
      `${process.env.REACT_APP_API_URL}/inventory/order/${id}/sim-activation-request`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error loading SIM activation request!' });
  }
});

export const patchNewOrderDetails = createAsyncThunk<
  OrderType,
  {
    id: number;
    data: {
      warehouse?: { id: number };
      status?: OrderStatus;
      qualityChecker?: {
        id: number;
        firstname: string;
        lastname: string;
        email?: string;
      };
      invoiceChecker?: {
        id: number;
        firstname: string;
        lastname: string;
        email?: string;
      };
      paymentChecker?: {
        id: number;
        firstname: string;
        lastname: string;
        email?: string;
      };
      nonSerializedItems?: Array<{ model: { id: number }; count: number }>;
      accessoryBundles?: Array<{ bundle: { id: number }; quantity: number }>;
      holdReason?: string;
    };
  },
  { state: RootReducerState }
>('orders/patchNewOrderDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<OrderType>(`${process.env.REACT_APP_API_URL}/inventory/order/${id}`, data);

    thunkAPI.dispatch(getNewOrderDetails({ id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating order!' });
  }
});

export const resumeOrderFromHold = createAsyncThunk<{ success: boolean }, { id: number }, { state: RootReducerState }>(
  'orders/resumeOrderFromHold',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.patch<{ success: boolean }>(
        `${process.env.REACT_APP_API_URL}/inventory/order/${id}/resume-from-hold`
      );

      thunkAPI.dispatch(getNewOrderDetails({ id }));

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during resuming order from hold!' });
    }
  }
);

export const getOrderWarehouses = createAsyncThunk<
  PaginationType<{ id: number; name: string }>,
  { orderId: number },
  { state: RootReducerState }
>('orders/getOrderWarehouses/no-loader', async ({ orderId }, thunkAPI) => {
  try {
    const response = await axios.get<PaginationType<{ id: number; name: string }>>(
      `${process.env.REACT_APP_API_URL}/inventory/order/${orderId}/warehouses`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching warehouses!' });
  }
});

export const getInventoryReportByWarehouse = createAsyncThunk<
  {
    atas: DeviceReportItemType[];
    batteries: DeviceReportItemType[];
    dialers: DeviceReportItemType[];
    enclosures: DeviceReportItemType[];
    powerCycles: DeviceReportItemType[];
    routers: DeviceReportItemType[];
    sims: DeviceReportItemType[];
    nonSerializedItems: DeviceReportItemType[];
  },
  { warehouseId: number },
  { state: RootReducerState }
>('orders/getInventoryReportByWarehouse/no-loader', async ({ warehouseId }, thunkAPI) => {
  try {
    const response = await axios.get<{
      atas: DeviceReportItemType[];
      batteries: DeviceReportItemType[];
      dialers: DeviceReportItemType[];
      enclosures: DeviceReportItemType[];
      powerCycles: DeviceReportItemType[];
      routers: DeviceReportItemType[];
      sims: DeviceReportItemType[];
      nonSerializedItems: DeviceReportItemType[];
    }>(`${process.env.REACT_APP_API_URL}/inventory/get-inventory`, {
      params: { warehouse_id: warehouseId },
    });
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching inventory report!' });
  }
});

export const getUsersByOrganizationId = createAsyncThunk<
  PaginationType<UserType>,
  { orgId: number },
  { state: RootReducerState }
>('orders/getUsersByOrganizationId/no-loader', async ({ orgId }, thunkAPI) => {
  try {
    const response = await axios.get<PaginationType<UserType>>(`${process.env.REACT_APP_API_URL}/users`, {
      params: { org_id: orgId, limit: 1000, is_active: 2, order: 'user.email' },
    });
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching users!' });
  }
});

export type DeviceAutocompleteRedirectResult =
  | { kind: 'ata'; id: number; ataType: ATA_TYPE }
  | { kind: 'battery' | 'dialer' | 'enclosure' | 'sim' | 'router' | 'powerCycle'; id: number };

export const getDeviceAutocompleteForRedirect = createAsyncThunk<
  DeviceAutocompleteRedirectResult | null,
  { category: DeviceCategory; serialNumber: string },
  { state: RootReducerState }
>('orders/getDeviceAutocompleteForRedirect/no-loader', async ({ category, serialNumber }, thunkAPI) => {
  const baseUrl = process.env.REACT_APP_API_URL;
  const q = encodeURIComponent(serialNumber);

  try {
    if (category === DeviceCategory.AtaAcs || category === DeviceCategory.AtaGs) {
      const response = await axios.get<Array<{ id: number; a_type: ATA_TYPE }>>(
        `${baseUrl}/atas/autocomplete?q=${q}&searchBy=serial_number`
      );
      if (response.data.length > 0) {
        return { kind: 'ata', id: response.data[0].id, ataType: response.data[0].a_type };
      }
      return null;
    }

    if (category === DeviceCategory.Battery) {
      const response = await axios.get<Array<{ id: number }>>(`${baseUrl}/battery/autocomplete?q=${q}&searchBy=serial`);
      if (response.data.length > 0) {
        return { kind: 'battery', id: response.data[0].id };
      }
      return null;
    }

    if (category === DeviceCategory.Dialer) {
      const response = await axios.get<Array<{ id: number }>>(
        `${baseUrl}/dialer/autocomplete?q=${q}&searchBy=radio_id`
      );
      if (response.data.length > 0) {
        return { kind: 'dialer', id: response.data[0].id };
      }
      return null;
    }

    if (category === DeviceCategory.Enclosure) {
      const response = await axios.get<Array<{ id: number }>>(`${baseUrl}/enclosures/autocomplete?q=${q}&searchBy=sn`);
      if (response.data.length > 0) {
        return { kind: 'enclosure', id: response.data[0].id };
      }
      return null;
    }

    if (category === DeviceCategory.Sim) {
      const response = await axios.get<Array<{ id: number }>>(`${baseUrl}/tc-sim/autocomplete?q=${q}&searchBy=iccid`);
      if (response.data.length > 0) {
        return { kind: 'sim', id: response.data[0].id };
      }
      return null;
    }

    if (category === DeviceCategory.Router) {
      const response = await axios.get<Array<{ id: number }>>(
        `${baseUrl}/routers/autocomplete?q=${q}&searchBy=serial_number`
      );
      if (response.data.length > 0) {
        return { kind: 'router', id: response.data[0].id };
      }
      return null;
    }

    if (category === DeviceCategory.PowerCycle) {
      const response = await axios.get<Array<{ id: number }>>(
        `${baseUrl}/power-cycles/autocomplete?q=${q}&searchBy=sn`
      );
      if (response.data.length > 0) {
        return { kind: 'powerCycle', id: response.data[0].id };
      }
      return null;
    }

    return null;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching device details!' });
  }
});

export const postOrderComment = createAsyncThunk<
  CommentType,
  { data: CreateOrderCommentDto },
  { state: RootReducerState }
>('orders/postOrderComment', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<CommentType>(`${process.env.REACT_APP_API_URL}/inventory/comment`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating order comment!' });
  }
});

export const getOrderTemplatesList = createAsyncThunk<
  PaginationType<OrderTemplateType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('orders/getOrderTemplatesList/no-loader', async (data, thunkAPI) => {
  const { controller, ...params } = data;
  try {
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      null,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<OrderTemplateType>>(
      `${process.env.REACT_APP_API_URL}/inventory/order-template${queryString ? `?${queryString}` : ''}`,
      { signal: controller?.signal }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching order templates!' });
  }
});

export const getNewOrdersList = createAsyncThunk<
  PaginationType<NewOrderType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('orders/getNewOrdersList/no-loader', async (data, thunkAPI) => {
  const { controller, ...params } = data;
  try {
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };

    const filteredParams: Record<string, string> = Object.keys(mergedParams).reduce((acc, key) => {
      const value = mergedParams[key];
      if (value !== '' && value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});
    const response = await axios.get<PaginationType<NewOrderType>>(`${process.env.REACT_APP_API_URL}/inventory/order`, {
      signal: controller?.signal,
      params: filteredParams,
    });
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching orders!' });
  }
});

export const getOrdersList = createAsyncThunk<
  PaginationType<OrderType>,
  RouteParamsType & {
    controller?: AbortController;
  },
  { state: RootReducerState }
>('orders/getOrdersList/no-loader', async (data, thunkAPI) => {
  const { controller, ...params } = data;
  try {
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<OrderType>>(
      `${process.env.REACT_APP_API_URL}/orders${queryString ? `?${queryString}` : ''}`,
      { signal: controller?.signal }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching orders!' });
  }
});

export const getOrderTemplateDetails = createAsyncThunk<OrderTemplateType, { id: number }, { state: RootReducerState }>(
  'orders/getOrderTemplateDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<OrderTemplateType>(
        `${process.env.REACT_APP_API_URL}/inventory/order-template/${id}`
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching order template!' });
    }
  }
);

export const getNewOrderDetails = createAsyncThunk<NewOrderType, { id: number }, { state: RootReducerState }>(
  'orders/getNewOrderDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<NewOrderType>(`${process.env.REACT_APP_API_URL}/inventory/order/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching order!' });
    }
  }
);

export const getOrderDetails = createAsyncThunk<OrderType, { id: number }, { state: RootReducerState }>(
  'orders/getOrderDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<OrderType>(`${process.env.REACT_APP_API_URL}/orders/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching order!' });
    }
  }
);

export const patchOrderDetails = createAsyncThunk<
  OrderType,
  { id: number; data: unknown },
  { state: RootReducerState }
>('orders/patchOrderDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<OrderType>(`${process.env.REACT_APP_API_URL}/orders/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating order!' });
  }
});

export const deleteOrderDetails = createAsyncThunk<
  OrderType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('orders/deleteOrderDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<OrderType>(`${process.env.REACT_APP_API_URL}/orders/${id}`);
    if (fetchList) thunkAPI.dispatch(getOrdersList(thunkAPI.getState().ordersReducer.ordersRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting order!' });
  }
});

export const deleteOrderTemplateDetails = createAsyncThunk<
  OrderTemplateType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('orders/deleteOrderTemplateDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<OrderTemplateType>(
      `${process.env.REACT_APP_API_URL}/inventory/order-template/${id}`
    );
    if (fetchList)
      thunkAPI.dispatch(getOrderTemplatesList(thunkAPI.getState().ordersReducer.orderTemplatesRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting order template!' });
  }
});

export const postOrdersTrackingInfoEmail = createAsyncThunk<OrderType, { id: number }, { state: RootReducerState }>(
  'orders/postOrdersTrackingInfoEmail',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.post<OrderType>(`${process.env.REACT_APP_API_URL}/orders/${id}/tracking-info-email`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during sending tracking info email!' });
    }
  }
);

export const postOrdersSimActivationEmail = createAsyncThunk<OrderType, { id: number }, { state: RootReducerState }>(
  'orders/postOrdersSimActivationEmail',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<OrderType>(`${process.env.REACT_APP_API_URL}/orders/${id}/sim-activation-email`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during sending sim activation email!' });
    }
  }
);

export const postAcceptOrder = createAsyncThunk<{ success: boolean }, { orderId: number }, { state: RootReducerState }>(
  'orders/postAcceptOrder',
  async ({ orderId }, thunkAPI) => {
    try {
      const response = await axios.patch<{ success: boolean }>(
        `${process.env.REACT_APP_API_URL}/inventory/order/${orderId}/accept`
      );

      thunkAPI.dispatch(
        getNewOrdersList({
          ...thunkAPI.getState().ordersReducer.newOrdersRouteParams,
          controller: new AbortController(),
        })
      );

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during accepting order!' });
    }
  }
);

export const deleteOrderFile = createAsyncThunk<
  OrderType,
  { id: number; order_file_id: number },
  { state: RootReducerState }
>('orders/deleteOrderFile', async ({ id, order_file_id }, thunkAPI) => {
  try {
    const response = await axios.delete<OrderType>(
      `${process.env.REACT_APP_API_URL}/orders/${id}/order-file/${order_file_id}`
    );
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting order file!' });
  }
});

// Action for updating CC notify users without triggering navigation
export const updateOrderCCNotifyUsers = createAsyncThunk<
  OrderType,
  { id: number; data: unknown },
  { state: RootReducerState }
>('orders/updateOrderCCNotifyUsers', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<OrderType>(`${process.env.REACT_APP_API_URL}/orders/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating CC notify users!' });
  }
});

export const updateInventoryOrderCCNotifyUsers = createAsyncThunk<
  { success: boolean },
  { id: number; data: { ccNotifyUsers: Array<{ id: number }> } },
  { state: RootReducerState }
>('orders/updateInventoryOrderCCNotifyUsers', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/inventory/order/${id}/cc-notify-users`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating CC notify users!' });
  }
});

export const updateOrderStatus = createAsyncThunk<any, any, { state: RootReducerState }>(
  'orders/updateOrderStatus',
  async ({ id, status }: { id: number; status: OrderStatus }, thunkAPI) => {
    try {
      const response = await axios.patch(`${process.env.REACT_APP_API_URL}/inventory/order/${id}`, { status });

      const orderDetails = { ...thunkAPI.getState().ordersReducer.newOrderDetails };
      orderDetails.status = status;
      thunkAPI.dispatch(setNewOrderDetailsFetchStatus(FETCH_STATUS.PENDING));
      thunkAPI.dispatch(setNewOrderDetails(orderDetails));
      thunkAPI.dispatch(setNewOrderDetailsFetchStatus(FETCH_STATUS.FULFILLED));

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating status!' });
    }
  }
);

export const updateOrderWarehouse = createAsyncThunk<
  OrderType,
  { id: number; warehouse: { id: number } },
  {
    state: RootReducerState;
  }
>('orders/updateOrderWarehouse', async ({ id, warehouse }, thunkAPI) => {
  try {
    const response = await axios.patch(`${process.env.REACT_APP_API_URL}/inventory/order/${id}`, {
      warehouse,
    });

    thunkAPI.dispatch(getNewOrderDetails({ id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating warehouse!' });
  }
});

export const patchOrderUpdateInvoiceStatus = createAsyncThunk<
  NewOrderType,
  { id: number; dto: { invoiceNumber: string; file: File | null } },
  { state: RootReducerState }
>('orders/patchOrderUpdateInvoiceStatus', async ({ id, dto }, thunkAPI) => {
  try {
    const formData = new FormData();
    formData.append('invoiceNumber', dto.invoiceNumber);
    if (dto.file) {
      formData.append('file', dto.file);
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/inventory/order/${id}/invoice-status`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    thunkAPI.dispatch(getNewOrderDetails({ id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating invoice status!' });
  }
});

export const patchOrderUpdatePaymentStatus = createAsyncThunk<
  NewOrderType,
  { id: number; dto: { paymentNumber?: string } },
  { state: RootReducerState }
>('orders/patchOrderUpdatePaymentStatus', async ({ id, dto }, thunkAPI) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/inventory/order/${id}/payment-status`, {
      paymentNumber: dto.paymentNumber,
    });

    thunkAPI.dispatch(getNewOrderDetails({ id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating payment status!' });
  }
});

export const confirmQACheck = createAsyncThunk<{ success: boolean }, { id: number }, { state: RootReducerState }>(
  'orders/confirmQACheck',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.patch<{ success: boolean }>(
        `${process.env.REACT_APP_API_URL}/inventory/order/${id}/confirm-qa`
      );

      thunkAPI.dispatch(getNewOrderDetails({ id }));

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during confirming QA check!' });
    }
  }
);

export const failQACheck = createAsyncThunk<NewOrderType, { id: number; reason: string }, { state: RootReducerState }>(
  'orders/failQACheck',
  async ({ id, reason }, thunkAPI) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/inventory/order/${id}/fail-qa`, { reason });

      thunkAPI.dispatch(getNewOrderDetails({ id }));

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during failing QA check!' });
    }
  }
);

export const cancelOrderDetails = createAsyncThunk<
  OrderType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('orders/cancelOrderDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.patch<OrderType>(`${process.env.REACT_APP_API_URL}/inventory/order/${id}/cancel`);
    if (fetchList) thunkAPI.dispatch(getOrdersList(thunkAPI.getState().ordersReducer.ordersRouteParams));
    thunkAPI.dispatch(getNewOrderDetails({ id }));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during canceling order!' });
  }
});

export const deleteInvenotryOrderDetails = createAsyncThunk<
  OrderType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('orders/deleteInvenotryOrderDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<OrderType>(`${process.env.REACT_APP_API_URL}/inventory/order/${id}`);
    if (fetchList) thunkAPI.dispatch(getNewOrdersList(thunkAPI.getState().ordersReducer.newOrdersRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting inventory order!' });
  }
});

export const downloadOrderDetails = createAsyncThunk<void, { orderId: number }, { state: RootReducerState }>(
  'orders/downloadOrderDetails',
  async ({ orderId }, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const { newOrderDetails } = state.ordersReducer;

      const exportData = {
        exportInfo: {
          exportedAt: new Date().toISOString(),
          exportedBy: state.authReducer.user?.email || 'Unknown',
          orderId: orderId,
        },
        orderDetails: newOrderDetails,
        summary: {
          totalComments: newOrderDetails?.comments?.length || 0,
          totalAttachments: newOrderDetails?.attachments?.length || 0,
          totalVirtualDevices: newOrderDetails?.virtualDevices?.length || 0,
          totalShippingPackages: newOrderDetails?.shippingPackages?.length || 0,
        },
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `order-${orderId}-details-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error downloading order details!' });
    }
  }
);

export const getRMAOrdersList = createAsyncThunk<
  PaginationType<RMAOrderListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('orders/getRMAOrdersList/no-loader', async (data, thunkAPI) => {
  const { controller, ...params } = data;
  try {
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      undefined
    );
    const mergedParams = { ...params, ...additionalParams };

    const response = await axios.get<PaginationType<RMAOrderListItemType>>(
      `${process.env.REACT_APP_API_URL}/inventory/rma-order`,
      { signal: controller?.signal, params: mergedParams }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching RMA orders!' });
  }
});

export const getRMAOrderDetails = createAsyncThunk<RMAOrderType, { id: number }, { state: RootReducerState }>(
  'orders/getRMAOrderDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<RMAOrderType>(`${process.env.REACT_APP_API_URL}/inventory/rma-order/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching rma order!' });
    }
  }
);

export const confirmRMAOrderDelivery = createAsyncThunk<
  { success: boolean },
  { id: number },
  { state: RootReducerState }
>('orders/confirmRMAOrderDelivery', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.patch<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/inventory/rma-order/${id}/confirm-delivery`
    );

    thunkAPI.dispatch(getRMAOrderDetails({ id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating rma order!' });
  }
});

export const deleteRMAOrderDetails = createAsyncThunk<
  { success: boolean },
  { id: number },
  { state: RootReducerState }
>('orders/deleteRMAOrderDetails', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.delete<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/inventory/rma-order/${id}`
    );
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting rma order!' });
  }
});
